export interface ParsedArticle {
  title: string;
  content: string;
  excerpt: string;
  author?: string;
  word_count?: number;
}

// ---- Failure cache --------------------------------------------------------
// Cache failed URLs with a TTL so a transient outage doesn't poison an
// article for the rest of the session.
const FAILURE_TTL_MS = 5 * 60 * 1000;
const failedUrls = new Map<string, number>();

const isFailureFresh = (url: string): boolean => {
  const failedAt = failedUrls.get(url);
  if (failedAt === undefined) return false;
  if (Date.now() - failedAt > FAILURE_TTL_MS) {
    failedUrls.delete(url);
    return false;
  }
  return true;
};

// ---- Success cache (LRU) --------------------------------------------------
// Bounded in-memory cache of successfully-parsed articles. Lives only for
// the page lifetime — no localStorage, no server-side cache. The cap is
// generous enough to cover a screenful of articles plus a few prefetched
// neighbours, but small enough that a session of scrolling can't blow up
// browser memory.
const PARSE_CACHE_CAP = 30;
const parseCache = new Map<string, ParsedArticle>();

const touch = (url: string, value: ParsedArticle): void => {
  // Re-inserting moves the entry to the "most recently used" end of the
  // insertion-ordered Map. When we exceed the cap, the first key (least
  // recently used) gets evicted.
  parseCache.delete(url);
  parseCache.set(url, value);
  while (parseCache.size > PARSE_CACHE_CAP) {
    const oldest = parseCache.keys().next().value;
    if (oldest === undefined) break;
    parseCache.delete(oldest);
  }
};

export const getCachedParse = (url: string): ParsedArticle | undefined => {
  const hit = parseCache.get(url);
  if (hit !== undefined) {
    // Touch on read so frequently-viewed entries don't get evicted.
    touch(url, hit);
  }
  return hit;
};

export const evictCachedParse = (url: string): void => {
  parseCache.delete(url);
};

export const clearParseCache = (): void => {
  parseCache.clear();
};

// ---- In-flight de-duplication ---------------------------------------------
// If two callers ask for the same URL while a fetch is in flight, share the
// promise so the server is only hit once. Important when the user hovers
// then clicks within the same network round-trip.
const inFlight = new Map<string, Promise<ParsedArticle | null>>();

// ---- Public API -----------------------------------------------------------

export const parseArticle = async (
  url: string,
  signal?: AbortSignal,
): Promise<ParsedArticle | null> => {
  const cached = getCachedParse(url);
  if (cached) return cached;
  if (isFailureFresh(url)) return null;

  const existing = inFlight.get(url);
  if (existing) return existing;

  const promise = (async (): Promise<ParsedArticle | null> => {
    try {
      // Only pass the init object when there's actually a signal — keeps
      // the call shape compatible with callers (and tests) that expect a
      // one-arg fetch invocation.
      const response = signal
        ? await fetch(`/api/parse-article?url=${encodeURIComponent(url)}`, { signal })
        : await fetch(`/api/parse-article?url=${encodeURIComponent(url)}`);

      if (!response.ok) {
        failedUrls.set(url, Date.now());
        return null;
      }

      const data = (await response.json()) as ParsedArticle;
      failedUrls.delete(url);
      touch(url, data);
      return data;
    } catch (err) {
      // Don't mark aborts as failures — the caller cancelled deliberately.
      const aborted = err instanceof Error && err.name === 'AbortError';
      if (!aborted) failedUrls.set(url, Date.now());
      return null;
    } finally {
      inFlight.delete(url);
    }
  })();

  inFlight.set(url, promise);
  return promise;
};

// Fire-and-forget parse intended for speculative prefetching. Never throws,
// never blocks the caller. Pass an AbortSignal so a fresh wave / page hide
// can cancel pending work.
export const prefetchArticle = (url: string, signal?: AbortSignal): void => {
  if (getCachedParse(url) || isFailureFresh(url) || inFlight.has(url)) return;
  // Swallow result — the goal is to populate the cache.
  void parseArticle(url, signal);
};
